import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import UpgradeModal from "../components/UpgradeModal";

const LockIcon = () => (
  <svg
    className="w-20 h-20 text-gray-400 mb-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const CrownIcon = () => (
  <svg
    className="w-8 h-8 text-yellow-400"
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 6L9 2 6 6 2 4l2 8h16l2-8-4 2-3-4z" />
    <path d="M4 12h16v2a4 4 0 01-4 4H8a4 4 0 01-4-4v-2z" />
  </svg>
);

const StarIcon = ({ className = "w-4 h-4" }) => (
  <svg
    className={`${className} text-yellow-400`}
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const Unauthorized = () => {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(true);
  };

  const handleGoBack = () => {
    navigate("/script"); // Go back to previous page
  };

  const handleGoToLogin = () => {
    localStorage.removeItem("login_data");
    localStorage.removeItem("login_token");
    navigate("/"); // Go to login page
  };

  const premiumFeatures = [
    "Everything in free, Plus",
    "More credits less cost",
    "User Activity & Traffic Insights",
    "Track Specified Pages",
    "ICP & PIS",
    "Auto Enrichment and Lead Nurturing",
    "Hands Off Hotleads",
    "Progressive Outbound Nurturing",
    "Hubspot Integration",
    "Salesforce Integration",
    "Apollo Integration",
    "Clay Integration",
    "Zoho CRM Integration",
    "Zapier Integration",
    "Integrate with other Lead Management products",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center ">
      <div className=" w-full bg-white  ">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] p-8 text-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-4 left-8 opacity-20">
            <StarIcon className="w-6 h-6" />
          </div>
          <div className="absolute top-8 right-12 opacity-20">
            <StarIcon className="w-4 h-4" />
          </div>
          <div className="absolute bottom-6 left-12 opacity-20">
            <StarIcon className="w-5 h-5" />
          </div>

          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 rounded-full backdrop-blur-sm mb-4">
              <CrownIcon />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Premium Feature
            </h1>
            <p className="text-white/90 text-lg">
              Unlock the full potential of our platform
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Lock Icon */}
          <div className="flex justify-center mb-6">
            <LockIcon />
          </div>

          {/* Main Message */}
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            You are unauthorized to view this module
          </h2>

          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            This feature is part of our{" "}
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#5e78f4] to-[#f14ce2]">
              Win+
            </span>{" "}
            plan. Upgrade your account to access advanced features and unlock
            your business potential.
          </p>

          {/* Premium Features List */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-8 text-left">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
              What you'll get with Win+:
            </h3>
            <ul className="space-y-3">
              {premiumFeatures.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <div className="w-2 h-2 bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] rounded-full mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <button
              onClick={handleUpgradeClick}
              className="w-full sm:w-auto bg-gradient-to-r from-[#5e78f4] to-[#f14ce2] text-white font-bold py-4 px-8 rounded-xl hover:opacity-90 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center group"
            >
              <CrownIcon />
              <span className="ml-2 group-hover:translate-x-0.5 transition-transform duration-300">
                Upgrade to Win+
              </span>
            </button>

            <button
              onClick={handleGoBack}
              className="w-full sm:w-auto bg-gray-200 text-gray-700 font-bold py-4 px-8 rounded-xl hover:bg-gray-300 transition-all duration-300 transform hover:scale-105"
            >
              Go To Dashboard
            </button>
            {/* <button
              onClick={handleGoToLogin}
              className="w-full sm:w-auto bg-gray-200 text-gray-700 font-bold py-4 px-8 rounded-xl hover:bg-gray-300 transition-all duration-300 transform hover:scale-105"
            >
              Go To Login
            </button> */}
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-sm text-blue-700">
              <span className="font-semibold">💡 Need help?</span> Contact our
              support team if you have questions about upgrading your plan.
            </p>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />
    </div>
  );
};

export default Unauthorized;
